import { useEffect, useEffectEvent, useRef } from 'react'
import { Redirect } from 'expo-router'
import { RetrySignOutErrorWall } from '@/src/components/ui/RetrySignOutErrorWall'
import { ScreenLoadingFallback } from '@/src/components/ui/ScreenLoadingFallback'
import { useAppSession } from '@/src/contexts/AppSessionContext'
import { useResolvedAuthState } from '@/src/hooks/useResolvedAuthState'
import { pendingSignupFirstNameQueryOptions } from '@/src/queries/auth-flow-queries'
import { queryKeys } from '@/src/queries/query-keys'
import { extractApiErrorMessage } from '@/src/utils/parseApiError'
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { CreateUserVariables } from '../hooks/useCreateUserMutation'
import {
  useCreateUserMutation,
  useCreateUserStatus,
} from '../hooks/useCreateUserMutation'
import { resolveAuthCompleteRoute } from '../utils/auth-complete-route'
import {
  buildCreateUserAttemptKey,
  clearReservedCreateUserAttempt,
  reserveCreateUserAttempt,
} from '../utils/create-user-attempt'
import { resolveCreateUserFirstName } from '../utils/create-user-name'

export default function AuthCompleteScreen() {
  const { t } = useTranslation(['common', 'setup'])
  const queryClient = useQueryClient()
  const {
    user,
    error: authError,
    isAuthenticated,
    isAuthLoading,
  } = useResolvedAuthState()
  const {
    backendUserError,
    logout,
    protectedRoute,
    refreshUserStatus,
    hasPurchased,
    isAccessPending,
    requiresPostPaymentOnboarding,
  } = useAppSession()

  const createUserMutation = useCreateUserMutation()
  const createUserStatus = useCreateUserStatus()
  const email = user?.email ?? null
  const createUserAttemptQueryKey = queryKeys.authFlow.createUserAttempt(
    user?.sub ?? null,
  )
  const autoCreateAttemptRef = useRef<string | null>(
    queryClient.getQueryData<string | null>(createUserAttemptQueryKey) ?? null,
  )
  const { data: pendingSignupFirstName, isPending: isPendingSignupFirstName } =
    useSuspenseQuery(pendingSignupFirstNameQueryOptions())

  const resolvedFirstName = resolveCreateUserFirstName({
    pendingSignupFirstName: pendingSignupFirstName ?? null,
    authUser: user,
  })

  const routeDecision = resolveAuthCompleteRoute({
    isAuthenticated,
    isAuthLoading,
    protectedRoute,
    isPendingSignupFirstName,
    hasResolvedFirstName: resolvedFirstName != null,
    createUserStatus,
    requiresPostPaymentOnboarding,
    hasPurchased,
    isAccessPending,
  })

  const fireCreateUser = useEffectEvent(
    (attemptKey: string, variables: CreateUserVariables) => {
      const clearAttempt = () => {
        clearReservedCreateUserAttempt(attemptKey)
        autoCreateAttemptRef.current = null
        queryClient.removeQueries({
          queryKey: createUserAttemptQueryKey,
          exact: true,
        })
      }

      if (!createUserMutation.isIdle) {
        return
      }

      createUserMutation.mutate(variables, {
        onSuccess: clearAttempt,
        onError: clearAttempt,
      })
    },
  )

  useEffect(() => {
    if (routeDecision !== 'auto-create') {
      return
    }

    const nextAttemptKey = buildCreateUserAttemptKey({
      email,
      firstName: resolvedFirstName,
    })

    if (autoCreateAttemptRef.current === nextAttemptKey) {
      return
    }

    if (!reserveCreateUserAttempt(nextAttemptKey)) {
      autoCreateAttemptRef.current = nextAttemptKey
      return
    }

    autoCreateAttemptRef.current = nextAttemptKey
    queryClient.setQueryData(createUserAttemptQueryKey, nextAttemptKey)
    fireCreateUser(nextAttemptKey, { email, firstName: resolvedFirstName })
  }, [
    createUserAttemptQueryKey,
    email,
    queryClient,
    resolvedFirstName,
    routeDecision,
  ])

  const errorMessage =
    extractApiErrorMessage(createUserMutation.error) ??
    (backendUserError != null &&
    typeof backendUserError === 'object' &&
    'message' in backendUserError &&
    typeof backendUserError.message === 'string'
      ? backendUserError.message
      : undefined) ??
    authError?.message ??
    t('setup:error_generic')

  if (routeDecision === 'loading' || routeDecision === 'auto-create') {
    return <ScreenLoadingFallback />
  }

  if (routeDecision === 'error') {
    return (
      <RetrySignOutErrorWall
        message={errorMessage}
        onRetry={refreshUserStatus}
        onSignOut={logout}
      />
    )
  }

  if (routeDecision === 'onboarding') {
    return <Redirect href="/onboarding" />
  }

  if (routeDecision === 'create-user') {
    return <Redirect href="/create-user" />
  }

  if (routeDecision === 'purchase') {
    return <Redirect href="/purchase" />
  }

  if (routeDecision === 'post-payment-onboarding') {
    return <Redirect href="/post-payment-onboarding" />
  }

  return <Redirect href="/(protected)/(account)/(tabs)" />
}
